import React, { useState } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareButtons component for social sharing
 * @param {Object} props
 * @param {string} props.title - Title to share
 * @param {string} props.text - Description text to share
 * @param {string} props.url - URL to share (defaults to current page)
 * @param {string} props.variant - 'icon' for icon-only, 'full' for icons with text
 * @param {string} props.className - Additional CSS classes
 */
export function ShareButtons({ 
  title, 
  text, 
  url = typeof window !== 'undefined' ? window.location.href : '', 
  variant = 'icon',
  className = ''
}) {
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareMessage = text || `Découvrez ${title} sur LEMO !`;
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareMessage);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Erreur lors de la copie');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareMessage,
          url: url
        });
      } catch (err) {
        // User cancelled or error
        if (err.name !== 'AbortError') {
          setShowShare(true);
        }
      }
    } else {
      setShowShare(true);
    }
  };

  const handleSocialShare = (platform) => {
    const link = shareLinks[platform];
    window.open(link, '_blank', 'width=600,height=400,noopener,noreferrer');
    setShowShare(false);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Share Button */}
      <button
        onClick={handleNativeShare}
        className="w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg bg-white/90 text-gray-600 hover:text-emerald-600 hover:bg-white"
        data-testid="share-button"
        title="Partager"
      >
        <Share2 className="w-5 h-5" />
      </button>

      {/* Share Modal */}
      {showShare && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowShare(false)}
          />
          
          {/* Share Panel */}
          <div className="absolute right-0 top-12 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 w-64 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Partager</h3>
              <button
                onClick={() => setShowShare(false)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Social buttons */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              {/* WhatsApp */}
              <button
                onClick={() => handleSocialShare('whatsapp')}
                className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="share-whatsapp"
              >
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">WhatsApp</span>
              </button>

              {/* Facebook */}
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="share-facebook"
              >
                <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">Facebook</span>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="share-twitter"
              >
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">X</span>
              </button>

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="share-copy"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  copied ? 'bg-emerald-500' : 'bg-gray-200'
                }`}>
                  {copied ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                <span className="text-xs text-gray-600">
                  {copied ? 'Copié !' : 'Copier'}
                </span>
              </button>
            </div>
            
            {/* URL Preview */}
            <div className="bg-gray-50 rounded-lg p-2 text-xs text-gray-500 truncate">
              {url}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ShareButtons;
